// services/riskScoringService.js

const riskScoringService = {
  // Critical MITRE techniques that pose high risk
  criticalTechniques: [
    'T1003', // OS Credential Dumping
    'T1078', // Valid Accounts
    'T1098', // Account Manipulation
    'T1136', // Create Account
    'T1543', // Create or Modify System Process
    'T1053', // Scheduled Task/Job
    'T1021', // Remote Services
    'T1486', // Data Encrypted for Impact
    'T1490', // Inhibit System Recovery
    'T1489', // Service Stop
    'T1040', // Network Sniffing
    'T1055', // Process Injection
    'T1574', // Hijack Execution Flow
  ],

  highRiskTechniques: [
    'T1547', // Boot or Logon Autostart Execution
    'T1548', // Abuse Elevation Control Mechanism
    'T1134', // Access Token Manipulation
    'T1070', // Indicator Removal
    'T1202', // Indirect Command Execution
    'T1218', // System Binary Proxy Execution
    'T1569', // System Services
    'T1190', // Exploit Public-Facing Application
  ],

  // Calculate risk score for an action
  calculateActionRisk(action, targets = []) {
    let score = 0;
    const factors = [];
    
    // 1. MITRE Technique Risk (0-3 points)
    if (action.mitreAttackTechnique) {
      const techniqueId = action.mitreAttackTechnique.id;
      
      if (this.criticalTechniques.includes(techniqueId)) {
        score += 3;
        factors.push({
          factor: 'Critical MITRE Technique',
          weight: 3,
          value: techniqueId,
          detail: `${techniqueId} is a critical-severity technique`
        });
      } else if (this.highRiskTechniques.includes(techniqueId)) {
        score += 2;
        factors.push({
          factor: 'High-Risk MITRE Technique',
          weight: 2,
          value: techniqueId,
          detail: `${techniqueId} is a high-risk technique`
        });
      } else if (techniqueId) {
        score += 1;
        factors.push({
          factor: 'MITRE Technique',
          weight: 1,
          value: techniqueId,
          detail: `Standard MITRE technique used`
        });
      }
      
      // Additional risk for specific tactics
      const tactic = action.mitreAttackTechnique.tactic;
      if (['Impact', 'Collection', 'Exfiltration'].includes(tactic)) {
        score += 1;
        factors.push({
          factor: 'High-Risk Tactic',
          weight: 1,
          value: tactic,
          detail: `${tactic} tactics pose elevated risk`
        });
      }
    }
    
    // 2. Action Result Impact (0-3 points)
    if (action.result === 'successful') {
      score += 3;
      factors.push({
        factor: 'Successful Execution',
        weight: 3,
        value: 'successful',
        detail: 'Action completed successfully without detection'
      });
    } else if (action.result === 'partial') {
      score += 2;
      factors.push({
        factor: 'Partial Success',
        weight: 2,
        value: 'partial',
        detail: 'Action partially succeeded'
      });
    } else if (action.result === 'detected') {
      score += 1;
      factors.push({
        factor: 'Detected but Executed',
        weight: 1,
        value: 'detected',
        detail: 'Action was detected but still executed'
      });
    }
    
    // 3. Target Criticality (0-3 points)
    if (targets && targets.length > 0) {
      const criticalTargets = targets.filter(t => 
        t.metadata?.criticality === 'critical'
      );
      const highTargets = targets.filter(t => 
        t.metadata?.criticality === 'high'
      );
      
      if (criticalTargets.length > 0) {
        score += 3;
        factors.push({
          factor: 'Critical Target',
          weight: 3,
          value: criticalTargets.length,
          detail: `Affected ${criticalTargets.length} critical target(s)`
        });
      } else if (highTargets.length > 0) {
        score += 2;
        factors.push({
          factor: 'High-Value Target',
          weight: 2,
          value: highTargets.length,
          detail: `Affected ${highTargets.length} high-value target(s)`
        });
      } else if (targets.length > 0) {
        score += 1;
        factors.push({
          factor: 'Standard Target',
          weight: 1,
          value: targets.length,
          detail: `Affected ${targets.length} target(s)`
        });
      }
      
      // Additional risk for production environments
      const prodTargets = targets.filter(t => 
        t.metadata?.environment === 'production'
      );
      if (prodTargets.length > 0) {
        score += 1;
        factors.push({
          factor: 'Production Environment',
          weight: 1,
          value: prodTargets.length,
          detail: `${prodTargets.length} production system(s) affected`
        });
      }
    }
    
    // 4. Special Target Types (0-2 points)
    if (targets && targets.length > 0) {
      const cloudTargets = targets.filter(t => t.type === 'cloudResource');
      const identityTargets = targets.filter(t => t.type === 'identity');
      const apiTargets = targets.filter(t => t.type === 'apiEndpoint');
      
      if (identityTargets.length > 0) {
        score += 2;
        factors.push({
          factor: 'Identity Target',
          weight: 2,
          value: identityTargets.length,
          detail: `Compromised ${identityTargets.length} identity/user account(s)`
        });
      }
      
      if (cloudTargets.length > 0) {
        score += 1;
        factors.push({
          factor: 'Cloud Resource',
          weight: 1,
          value: cloudTargets.length,
          detail: `Affected ${cloudTargets.length} cloud resource(s)`
        });
      }
      
      if (apiTargets.length > 0) {
        score += 1;
        factors.push({
          factor: 'API Endpoint',
          weight: 1,
          value: apiTargets.length,
          detail: `Compromised ${apiTargets.length} API endpoint(s)`
        });
      }
    }
    
    // 5. Persistence or Lateral Movement (0-2 points)
    if (action.mitreAttackTechnique) {
      const tactic = action.mitreAttackTechnique.tactic;
      if (tactic === 'Persistence') {
        score += 2;
        factors.push({
          factor: 'Persistence Established',
          weight: 2,
          value: 'persistence',
          detail: 'Established persistent access'
        });
      } else if (tactic === 'Lateral Movement') {
        score += 2;
        factors.push({
          factor: 'Lateral Movement',
          weight: 2,
          value: 'lateral',
          detail: 'Achieved lateral movement in network'
        });
      }
    }
    
    // Calculate final score (normalize to 0-10)
    const normalizedScore = Math.min(10, score);
    
    // Determine risk level
    let level;
    if (normalizedScore >= 9) level = 'Critical';
    else if (normalizedScore >= 7) level = 'High';
    else if (normalizedScore >= 5) level = 'Medium';
    else if (normalizedScore >= 3) level = 'Low';
    else level = 'Minimal';
    
    // Generate recommendations based on risk factors
    const recommendations = this.generateRecommendations(factors, action);
    
    return {
      score: normalizedScore,
      level,
      factors,
      recommendations,
      summary: this.generateRiskSummary(normalizedScore, level, factors)
    };
  },
  
  // Generate mitigation recommendations
  generateRecommendations(factors, action) {
    const recommendations = [];
    
    // Check for critical techniques
    if (factors.some(f => f.factor === 'Critical MITRE Technique')) {
      recommendations.push({
        priority: 'immediate',
        category: 'detection',
        message: 'Implement enhanced monitoring for critical technique indicators'
      });
      
      const techniqueId = action.mitreAttackTechnique?.id;
      if (techniqueId === 'T1003') {
        recommendations.push({
          priority: 'immediate',
          category: 'prevention',
          message: 'Enable Credential Guard and restrict LSASS access'
        });
      } else if (techniqueId === 'T1486') {
        recommendations.push({
          priority: 'immediate',
          category: 'prevention',
          message: 'Ensure offline backups and implement ransomware protections'
        });
      }
    }
    
    // Check for successful actions
    if (factors.some(f => f.factor === 'Successful Execution')) {
      recommendations.push({
        priority: 'high',
        category: 'detection',
        message: 'Review detection rules - this attack succeeded without alerts'
      });
    }
    
    // Check for critical targets
    if (factors.some(f => f.factor === 'Critical Target')) {
      recommendations.push({
        priority: 'immediate',
        category: 'protection',
        message: 'Implement additional access controls for critical assets'
      });
    }
    
    // Check for identity compromises
    if (factors.some(f => f.factor === 'Identity Target')) {
      recommendations.push({
        priority: 'immediate',
        category: 'response',
        message: 'Reset affected credentials and enforce MFA'
      });
    }
    
    // Check for persistence
    if (factors.some(f => f.factor === 'Persistence Established')) {
      recommendations.push({
        priority: 'immediate',
        category: 'response',
        message: 'Hunt for persistence mechanisms and remove backdoors'
      });
    }
    
    return recommendations;
  },
  
  // Generate risk summary
  generateRiskSummary(score, level, factors) {
    const topFactors = factors
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 3)
      .map(f => f.factor);
    
    let summary = `Risk Level: ${level} (Score: ${score}/10). `;
    
    if (score >= 7) {
      summary += 'This action represents a critical security risk. ';
    } else if (score >= 5) {
      summary += 'This action poses a significant security concern. ';
    } else {
      summary += 'This action has moderate security implications. ';
    }
    
    if (topFactors.length > 0) {
      summary += `Key risk factors: ${topFactors.join(', ')}.`;
    }
    
    return summary;
  },
  
  // Calculate project-wide risk score
  calculateProjectRisk(actions, targets) {
    const actionRisks = actions.map(action => {
      const actionTargets = targets.filter(t => 
        action.targets?.includes(t._id)
      );
      return this.calculateActionRisk(action, actionTargets);
    });
    
    const avgScore = actionRisks.length > 0
      ? actionRisks.reduce((sum, risk) => sum + risk.score, 0) / actionRisks.length
      : 0;
    
    const criticalCount = actionRisks.filter(r => r.level === 'Critical').length;
    const highCount = actionRisks.filter(r => r.level === 'High').length;
    
    // Adjust project score based on critical findings
    let projectScore = avgScore;
    if (criticalCount > 5) projectScore = Math.min(10, projectScore + 2);
    else if (criticalCount > 2) projectScore = Math.min(10, projectScore + 1);
    
    let level;
    if (projectScore >= 8) level = 'Critical';
    else if (projectScore >= 6) level = 'High';
    else if (projectScore >= 4) level = 'Medium';
    else level = 'Low';
    
    return {
      score: parseFloat(projectScore.toFixed(1)),
      level,
      criticalActions: criticalCount,
      highRiskActions: highCount,
      totalActions: actions.length,
      riskDistribution: {
        critical: actionRisks.filter(r => r.level === 'Critical').length,
        high: actionRisks.filter(r => r.level === 'High').length,
        medium: actionRisks.filter(r => r.level === 'Medium').length,
        low: actionRisks.filter(r => r.level === 'Low').length,
        minimal: actionRisks.filter(r => r.level === 'Minimal').length
      }
    };
  },
  
  // Get risk trend over time
  getRiskTrend(actions, timeWindow = 30) {
    const now = new Date();
    const windowStart = new Date(now.getTime() - (timeWindow * 24 * 60 * 60 * 1000));
    
    const trend = [];
    for (let i = 0; i < timeWindow; i++) {
      const date = new Date(windowStart.getTime() + (i * 24 * 60 * 60 * 1000));
      const dayActions = actions.filter(a => {
        const actionDate = new Date(a.startTimestamp || a.timestamp);
        return actionDate.toDateString() === date.toDateString();
      });
      
      const dayRisks = dayActions.map(action => 
        this.calculateActionRisk(action).score
      );
      
      trend.push({
        date: date.toISOString().split('T')[0],
        avgRisk: dayRisks.length > 0 
          ? dayRisks.reduce((a, b) => a + b, 0) / dayRisks.length 
          : 0,
        actionCount: dayActions.length
      });
    }
    
    return trend;
  }
};

export default riskScoringService;